import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageCircle, X, Send, Bot, User, ArrowRight, 
  Loader2, Sparkles
} from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * HelpChatbot Component
 * AI-powered smart chatbot that can answer any question about GlobalVyapar
 */
export default function HelpChatbot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'bot',
      content: "Hi! I'm your GlobalVyapar AI assistant. I can answer any questions about the platform, help you navigate, or explain trade terms. How can I help you today?",
      suggestions: ['How to add a product?', 'What is MOQ?', 'How do I get verified?', 'Show me market trends']
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [isOpen]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Navigation map for quick access
  const navigationMap = {
    'dashboard': '/dashboard',
    'home': '/dashboard',
    'products': '/products',
    'my products': '/products',
    'marketplace': '/marketplace',
    'browse': '/marketplace',
    'browse products': '/marketplace',
    'inquiries': '/inquiries',
    'messages': '/inquiries',
    'leads': '/leads',
    'profile': '/profile',
    'settings': '/settings',
    'watchlist': '/buyer-dashboard',
    'buyer dashboard': '/buyer-dashboard',
    'market trends': '/market-trends',
    'trends': '/market-trends',
    'support': '/support',
    'help': '/support',
    'privacy policy': '/privacy-policy',
    'terms of service': '/terms-of-service',
  };
  
  // Check if message contains navigation intent
  const checkForNavigation = (text) => {
    const lowerText = text.toLowerCase();
    
    for (const [key, path] of Object.entries(navigationMap)) {
      if (lowerText.includes(`go to ${key}`) || lowerText.includes(`take me to ${key}`) || lowerText.includes(`show me ${key}`) || lowerText.includes(`open ${key}`)) {
        return path;
      }
    }
    return null;
  };
  
  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    
    // Update conversation history
    const newHistory = [...conversationHistory, { role: 'user', content: input }];
    setConversationHistory(newHistory);
    
    // Check for direct navigation
    const navPath = checkForNavigation(input);
    if (navPath) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'bot',
          content: `Sure! Taking you there now...`,
          suggestions: []
        }]);
        setIsTyping(false);
        
        setTimeout(() => {
          navigate(navPath);
          setIsOpen(false);
        }, 800);
      }, 300);
      return;
    }
    
    // Call AI chatbot API
    try {
      const response = await axios.post(`${API}/ai/chatbot`, {
        message: input,
        conversation_history: newHistory.slice(-6),
        user_role: user?.role || null
      });
      
      const botMessage = {
        id: Date.now() + 1,
        role: 'bot',
        content: response.data.response,
        suggestions: response.data.suggestions || []
      };
      
      setMessages(prev => [...prev, botMessage]);
      setConversationHistory([...newHistory, { role: 'bot', content: response.data.response }]);
      
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'bot',
        content: "I'm here to help! You can ask me about:\n- How to use the platform\n- Trade terms (MOQ, FOB, CIF)\n- Navigation help\n- Account verification\n\nOr contact support@globalvyapar.co.in",
        suggestions: ['Show Dashboard', 'Browse Products', 'Contact Support']
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleSuggestionClick = async (suggestion) => {
    setInput('');
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: suggestion
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    
    const newHistory = [...conversationHistory, { role: 'user', content: suggestion }];
    setConversationHistory(newHistory);
    
    // Check for direct navigation
    const navPath = checkForNavigation(suggestion);
    if (navPath) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'bot',
          content: `Sure! Taking you there now...`,
          suggestions: []
        }]);
        setIsTyping(false);
        
        setTimeout(() => {
          navigate(navPath);
          setIsOpen(false);
        }, 800);
      }, 300);
      return;
    }
    
    // Call AI chatbot API
    try {
      const response = await axios.post(`${API}/ai/chatbot`, {
        message: suggestion,
        conversation_history: newHistory.slice(-6),
        user_role: user?.role || null
      });
      
      const botMessage = {
        id: Date.now() + 1,
        role: 'bot',
        content: response.data.response,
        suggestions: response.data.suggestions || []
      };
      
      setMessages(prev => [...prev, botMessage]);
      setConversationHistory([...newHistory, { role: 'bot', content: response.data.response }]);
      
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'bot',
        content: "Let me help you with that! Feel free to ask any question about GlobalVyapar.",
        suggestions: ['How to get started?', 'Browse products', 'Contact support']
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-4 right-4 z-50">
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            data-testid="chatbot-trigger"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        )}
      </div>
      
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[calc(100vw-32px)]">
          <Card className="shadow-2xl border-0 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold">AI Assistant</CardTitle>
                    <p className="text-xs text-white/80">Powered by GlobalVyapar</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-white hover:bg-white/20"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-0">
              <ScrollArea className="h-[350px] p-4">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] ${message.role === 'user' ? 'order-1' : 'order-2'}`}>
                        <div className={`flex items-start gap-2 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            message.role === 'user' ? 'bg-primary text-white' : 'bg-purple-100 text-purple-600'
                          }`}>
                            {message.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                          </div>
                          <div className={`rounded-2xl px-4 py-2.5 ${
                            message.role === 'user' 
                              ? 'bg-primary text-white rounded-tr-sm' 
                              : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                          }`}>
                            <p className="text-sm whitespace-pre-line">{message.content}</p>
                          </div>
                        </div>
                        
                        {/* Suggestions */}
                        {message.suggestions && message.suggestions.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2 ml-9">
                            {message.suggestions.map((suggestion, idx) => (
                              <Button
                                key={idx}
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs rounded-full bg-white hover:bg-purple-50 hover:text-purple-600 hover:border-purple-300"
                                onClick={() => handleSuggestionClick(suggestion)}
                              >
                                {suggestion}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing indicator */}
                  {isTyping && (
                    <div className="flex items-start gap-2">
                      <div className="h-7 w-7 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                        <Bot className="h-4 w-4" />
                      </div>
                      <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              
              {/* Input */}
              <div className="border-t p-3 bg-slate-50">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything..."
                    className="flex-1 rounded-full bg-white"
                    disabled={isTyping}
                    data-testid="chatbot-input"
                  />
                  <Button 
                    onClick={handleSend} 
                    disabled={!input.trim() || isTyping}
                    className="rounded-full h-10 w-10 p-0 bg-gradient-to-r from-purple-600 to-blue-600"
                    data-testid="chatbot-send"
                  >
                    {isTyping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  AI-powered support - available 24/7
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
